import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PreviewGrid } from '../../../components/PreviewGrid';
import { PreviewTable } from '../../../components/PreviewTable';
import { ProfileCard } from '../../../components/profile/ProfileCard';
import { TextCell } from '../../../components/table/TextCell';
import { formatDateOnly, formatDateTime } from '../../../lib/formatDate';
import { formatMoney } from '../../../lib/formatMoney';
import { SUBJECT_VALUE } from '../approvalTypes';
import type {
  ApprovalRequestDetail,
  ApprovalSubjectTable,
  ApprovalSubjectValueKind,
} from '../approvalTypes';

/**
 * Where a subject's own record lives, by the entity type the engine stamps on the request.
 *
 * **The engine does not know a module's URLs** — that is the whole reason a request carries an
 * entity type and an id rather than a link — so the ROUTE is resolved here, on the one page
 * that needs one. A module with no entry simply gets no Open entry, which is the
 * absent-callback idiom rather than a dead control.
 */
const SUBJECT_ROUTE: Record<string, (id: string) => string> = {
  PurchaseRequest: (id) => `/procurement/purchasing/purchase-requests/${id}`,
};

/**
 * What is being approved — the subject's own detail, mirrored onto the approval profile.
 *
 * **It renders whatever the owning module composed, and knows nothing about any module.** The
 * shape is titled sections of labelled facts plus tables of cells, so a purchase request and
 * whatever workflow follows it draw through this one component — which is what keeps the
 * engine's decoupling intact while still putting the record in front of the approver.
 *
 * **It is the FIRST card on the Info tab**, above the engine's own Request card. An approver
 * opens this page to decide, and what they are deciding on is the subject rather than the
 * routing — the engine's facts explain how the decision is taken, not what it is about.
 *
 * **Three states, and all three say something.** The detail, "hidden" for a reader without the
 * module's key, and "nothing more" where the module had only its one-line summary. A card that
 * silently vanished in the last two would leave a reader unable to tell a permission they lack
 * from a record that says nothing.
 */
export function SubjectDetailCard({
  request,
  canReadSubject,
}: {
  request: ApprovalRequestDetail;
  /**
   * Whether this reader holds the owning module's read key.
   *
   * Passed in rather than read here, because the page already resolves it and the answer is
   * what decides between the two empty states — asking twice is two chances to disagree.
   */
  canReadSubject: boolean;
}) {
  const navigate = useNavigate();
  const detail = request.subjectDetail;

  /*
    THE COG CARRIES ONE ENTRY AND NO EDIT, which is `<ProfileCard>`'s third exemption read
    literally: a card over ANOTHER module's record links out to that record rather than
    editing it in place — and that exemption is explicitly "no edit of the other module's
    fields", never "no cog". A card the reader cannot get from to the record itself is a
    dead end on the one page they most need the way out of.

    Gated on the module's own read key and on there BEING a route: the entry is absent
    rather than disabled, because it does not apply to that reader or that workflow at all.
  */
  const route = SUBJECT_ROUTE[request.entityType];

  const menuItems =
    canReadSubject && route
      ? [
          {
            key: 'open',
            label: `Open ${detail?.heading.toLowerCase() ?? 'record'}`,
            icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />,
            onClick: () => navigate(route(request.subjectId)),
          },
        ]
      : undefined;

  /*
    `!= null`, never `!== null`. `DefaultIgnoreCondition = WhenWritingNull` omits the property
    entirely, so a withheld or absent detail arrives as `undefined` and a strict test would
    report every request as carrying one. The rule CLAUDE.md states, applied at the seam.
  */
  if (detail == null) {
    return (
      <ProfileCard
        id="approval-profile-subject"
        title="What is being approved"
        menuItems={menuItems}
        padded
      >
        <p className="text-xs text-slate-600">
          {canReadSubject
            ? /*
                The module answered with nothing — the record is gone, or its summary IS the
                whole story. The summary is still on the header and on the Request card, so
                the reader is not left with nothing.
              */
              `The ${request.workflowName.toLowerCase()} module supplied no further detail for this request.`
            : /*
                HIDDEN, not an em dash. The record HAS contents; this page has not been given
                them — the distinction the issuance profile's signatories already draw.
              */
              `Hidden. Seeing what this ${request.workflowName.toLowerCase()} contains needs its own module's read permission.`}
        </p>
      </ProfileCard>
    );
  }

  return (
    /*
      PADDED, which is the one place this card departs from "the body has NO padding and the
      table fills it edge to edge". That rule is for a card holding ONE table that draws its
      own edges; this one holds several TITLED sections and a grid under them, and
      `<PreviewTable>` drops its border inside a `<ProfileCard>` — so unpadded the section
      headings would sit flush against the card's border with nothing between them and it.
    */
    <ProfileCard id="approval-profile-subject" title={detail.heading} menuItems={menuItems} padded>
      <div className="flex flex-col gap-5">
        {/*
          ONE `<PreviewTable>` WITH SECTIONS, not one per section. That is the component's own
          way of saying "one table, one treatment, several headings" — the shape the stock
          detail panel already uses — and several tables stacked would be several treatments
          that are free to drift apart.
        */}
        <PreviewTable
          variant="table-display"
          sections={detail.sections.map((section) => ({
            title: section.title,
            rows: section.facts.map((fact) => ({
              label: fact.label,
              value: renderValue(fact.value, fact.kind),
              mono: isMono(fact.kind),
            })),
          }))}
        />

        {/*
          The grids sit INSIDE the same card, under the facts they belong to — a second card
          would file one record's lines as though they were a section of their own.
        */}
        {detail.tables.map((table) => (
          <SubjectTable key={table.heading} table={table} />
        ))}
      </div>
    </ProfileCard>
  );
}

/**
 * One of the subject's child collections.
 *
 * **A `<PreviewGrid>`, not a `<DataTablePanel>`**, on that shell's own test: this collection is
 * not WRITTEN from where it is drawn. Nothing on an approval profile edits a purchase request,
 * so search, sort, paging, selection and a row kebab would all be chrome over rows that are
 * already on screen.
 */
function SubjectTable({ table }: { table: ApprovalSubjectTable }) {
  /*
    THE ROW'S POSITION IS ITS KEY, and that is honest here rather than lazy: these rows are a
    positional projection with no id on the wire — the module sends cells, not records — and
    nothing on this page reorders, filters or removes them. Resolved through a map rather than
    `indexOf`, which is O(n²) and compares by reference on every render.
  */
  const keys = new Map(table.rows.map((row, index) => [row, String(index)]));

  return (
    <PreviewGrid<(string | null)[]>
      title={table.heading}
      columns={table.columns.map((column, index) => ({
        header: column.header,
        width: column.width,
        align: isNumeric(column.kind) ? ('right' as const) : ('left' as const),
        /*
          The cell reads its value POSITIONALLY, which is what lets one component render a
          collection whose columns it does not know. `<TextCell>` still owns the treatment —
          the mono face, the muted zero, the overflow tooltip — so these rows behave like every
          other table cell in the product rather than being bare strings.
        */
        render: (row: (string | null)[]) => (
          <TextCell
            value={renderValue(row[index], column.kind)}
            mono={isMono(column.kind)}
            numeric={isNumeric(column.kind)}
          />
        ),
      }))}
      rows={table.rows}
      rowKey={(row) => keys.get(row) ?? '0'}
      empty={table.empty}
    />
  );
}

/**
 * Formats a raw wire value the way this product formats that kind of value.
 *
 * **THE FORMATTERS ARE THE PRODUCT'S OWN** — `formatMoney`, `formatDateOnly`, `formatDateTime`
 * — which is the whole reason the module sends `1100.00` rather than a grouped string: an
 * amount formatted at its call site is one that will be formatted differently at the next call
 * site, and this panel sits one click from the module's own page showing the same figure.
 */
function renderValue(value: string | null | undefined, kind: ApprovalSubjectValueKind): string | null {
  if (value == null || value === '') return null;

  switch (kind) {
    case SUBJECT_VALUE.money: {
      const amount = Number(value);

      /*
        An unparseable amount falls back to the raw string rather than to `formatMoney`'s em
        dash: the module sent SOMETHING, and swallowing it would report the record as having
        no value when what it has is a value this page could not read.
      */
      return Number.isFinite(amount) ? formatMoney(amount) : value;
    }

    // A calendar date is NOT an instant and must not be converted — a reader west of UTC
    // would be shown the day before the one on the record.
    case SUBJECT_VALUE.date:
      return formatDateOnly(value);

    case SUBJECT_VALUE.dateTime:
      return formatDateTime(value);

    default:
      return value;
  }
}

/** Every machine value takes the mono face: a code, a date, an amount, a quantity. */
function isMono(kind: ApprovalSubjectValueKind): boolean {
  return kind !== SUBJECT_VALUE.text;
}

/**
 * Only a QUANTITY and an AMOUNT are numeric.
 *
 * `numeric` right-aligns and MUTES A ZERO, so it is deliberately narrower than `mono`: a code
 * and a date are machine values whose zero means nothing of the kind — the distinction the
 * table conventions already draw between `YearGraduated` and a count.
 */
function isNumeric(kind: ApprovalSubjectValueKind): boolean {
  return kind === SUBJECT_VALUE.quantity || kind === SUBJECT_VALUE.money;
}
